import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { FormattedDataSet } from '@mtna/data-core-ui/public_api';
import { MtnaLogger } from '@mtna/lib-ui';

import {
  SelectParameters,
  serializeSelectOptionsUrlParams,
  serializeTabulationOptionsUrlParams,
  TabulationParameters,
} from '../models/index';
import { RdsApiService } from './rds-api.service';

@Injectable({
  providedIn: 'root'
})
export class RdsQueryService {
  /** base url for the query service */
  protected get QUERY_URL(): string {
    return `${this.apiService.getApiUrl()}/query`;
  }

  /** url for the selected data product */
  protected get DATA_PRODUCT_URL(): string {
    return `${this.QUERY_URL}/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`;
  }

  protected _countCache = new Map<string, number>();
  protected _selectCache = new Map<string, any>();
  protected _tabulateCache = new Map<string, any>();

  constructor(protected apiService: RdsApiService, protected http: HttpClient, protected logger: MtnaLogger) {}

  /**
   * Get record count. Runs a query to count the records of the specified data product.
   * @remarks This method uses caching, returns a cold observable.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the count of records for the `RdsApiService`'s selected data product
   */
  count(refresh: boolean = false): Observable<number> {
    const mapKey = this._getCatalogDataProductMapKey();
    if (refresh) {
      this.logger.debug(`[RdsQueryService] count - Refresh requested, removing ${mapKey} from cache`);
      this._countCache.delete(mapKey);
    }
    if (this._countCache.has(mapKey)) {
      this.logger.debug(`[RdsQueryService] count - Returning ${mapKey} from cache`);
      return of(this._countCache.get(mapKey));
    }
    this.logger.debug(`[RdsQueryService] count - Fetching, ${mapKey} not in cache`);
    return this.http.get<number>(`${this.DATA_PRODUCT_URL}/count`).pipe(tap(num => this._countCache.set(mapKey, num)));
  }

  /**
   * Returns the select results. This can be in a different shape based on the `format` parameter.
   * By default it will return a {@link SimplifiedDataSet}, which is the equivalent of using `format=mtna_simple`.
   * A `format=MTNA` will return a {@link DataSet}.
   *
   * Valid format parameters are: amcharts, gcharts, mtna, mtna_simpl,
   * plotly_area, plotly_bar, plotly_boxplot, plotly_bubble, plotly_hist, plotly_h_bar, plotly_h_hist, plotly_line,
   * plotly_pie, plotly_scatter, plotly_ts
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various select parameters
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns record level mircodata for the `RdsApiService`'s selected data product
   */
  select<DS extends FormattedDataSet>(params: SelectParameters, refresh: boolean = false): Observable<DS> {
    const path = `${this.DATA_PRODUCT_URL}/select?${serializeSelectOptionsUrlParams(params)}`;
    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsQueryService] select - Refresh requested, removing ${mapKey} from cache`);
      this._selectCache.delete(mapKey);
    }
    if (this._selectCache.has(mapKey)) {
      this.logger.debug(`[RdsQueryService] select - Returning ${mapKey} from cache`);
      return of(this._selectCache.get(mapKey));
    }
    this.logger.debug(`[RdsQueryService] select - Fetching, ${mapKey} not in cache`);
    return this.http.get<DS>(path).pipe(tap(ds => this._selectCache.set(mapKey, ds)));
  }

  /**
   * Returns the tabulation results. This can be in a different shape based on the `format` parameter.
   * By default it will return a {@link SimplifiedDataSet}, which is the equivalent of using `format=mtna_simple`.
   * A `format=MTNA` will return a {@link DataSet}.
   *
   * Valid format parameters are: amcharts, gcharts, mtna, mtna_simpl,
   * plotly_area, plotly_bar, plotly_boxplot, plotly_bubble, plotly_hist, plotly_h_bar, plotly_h_hist, plotly_line,
   * plotly_pie, plotly_scatter, plotly_ts
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various tabulation parameters
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns The result will be an extension of a FormattedDataSet. This is based on the `format` parameter.
   * By default it will return a {@link SimplifiedDataSet}
   */
  tabulate<DS extends FormattedDataSet>(params: TabulationParameters, refresh: boolean = false): Observable<DS> {
    const path = `${this.DATA_PRODUCT_URL}/tabulate?${serializeTabulationOptionsUrlParams(params)}`;
    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsQueryService] tabulate - Refresh requested, removing ${mapKey} from cache`);
      this._tabulateCache.delete(mapKey);
    }
    if (this._tabulateCache.has(mapKey)) {
      this.logger.debug(`[RdsQueryService] tabulate - Returning ${mapKey} from cache`);
      return of(this._tabulateCache.get(mapKey));
    }
    this.logger.debug(`[RdsQueryService] tabulate - Fetching, ${mapKey} not in cache`);
    return this.http.get<DS>(path).pipe(tap(ds => this._tabulateCache.set(mapKey, ds)));
  }

  /**
   * Combines the ID of the `RdsApiService`'s selected catalog and data product to be used as a cache map key.
   * @returns map key for a specific catalog and data product
   */
  private _getCatalogDataProductMapKey(): string {
    return `${this.apiService.getCatalogId()}.${this.apiService.getDataProductId()}`;
  }
}
